/**
 * MSPDI 日期／日期時間字串 <-> ISODate / ISODateTime 的雙向轉換。
 *
 * 契約規定日期一律為 local wall-clock、不帶時區。MSPDI 的日期格式本身
 * 就是 `YYYY-MM-DDTHH:mm:ss`（可能帶毫秒或 `Z` 後綴），因此轉換只需
 * 正規化格式、去除時區／毫秒資訊，不做任何時區運算。
 *
 * 穩健性：外部 XML 常見「形狀合法、語意非法」的日期（例如 `2026-02-31`、
 * `2026-13-01`、`25:00:00`）。本檔案除了 regex 形狀比對外，一律再做一次
 * 真實的日曆語意驗證（月份 1-12、日期不超過該月天數、時分秒範圍），
 * 驗證失敗一律回傳 `null`，絕不讓呼叫端拿到一個看似合法但實際錯誤的日期，
 * 也絕不 throw —— 即使未來 branded 建構子本身加上驗證會 throw，這裡也用
 * try/catch 兜底，確保單一壞日期不會中止整份 MSPDI 匯入。
 */

import {
  isoDate,
  isoDateTime,
  rawString,
  type ISODate,
  type ISODateTime,
} from '../../domain/branded.js';

const DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/;
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** 驗證年/月/日/時/分/秒是否為真實存在的日曆日期時間（而非只符合 regex 形狀）。 */
function isValidCalendarDateTime(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): boolean {
  if (!Number.isInteger(year) || year < 1 || year > 9999) {
    return false;
  }
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return false;
  }
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    return false;
  }
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) {
    return false;
  }
  if (!Number.isInteger(second) || second < 0 || second > 59) {
    return false;
  }
  if (!Number.isInteger(day) || day < 1) {
    return false;
  }
  // Date.UTC(year, month, 0) 的「第 0 天」等於 `month`（1-based）的前一個月最後一天，
  // 亦即該 1-based 月份的天數，藉此判斷 day 是否超過當月天數（含閏年 2 月）。
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day <= daysInMonth;
}

/** 解析 MSPDI 日期時間字串；形狀或語意（例：2 月 31 日）不合法一律回傳 null，絕不 throw。 */
export function parseMspdiDateTime(value: string | undefined | null): ISODateTime | null {
  if (value === undefined || value === null) {
    return null;
  }
  try {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }
    const match = DATETIME_RE.exec(trimmed);
    if (match) {
      const [, y, mo, d, hh, mm, ss] = match as unknown as [string, string, string, string, string, string, string];
      if (!isValidCalendarDateTime(Number(y), Number(mo), Number(d), Number(hh), Number(mm), Number(ss))) {
        return null;
      }
      return isoDateTime(`${y}-${mo}-${d}T${hh}:${mm}:${ss}`);
    }
    const dateOnly = DATE_ONLY_RE.exec(trimmed);
    if (dateOnly) {
      const [, y, mo, d] = dateOnly as unknown as [string, string, string, string];
      if (!isValidCalendarDateTime(Number(y), Number(mo), Number(d), 0, 0, 0)) {
        return null;
      }
      return isoDateTime(`${y}-${mo}-${d}T00:00:00`);
    }
    return null;
  } catch {
    // 防禦性兜底：即使上游 branded 建構子未來改為會驗證並 throw，這裡也絕不讓例外
    // 往外傳播 —— 單一壞日期只能造成這個欄位解析失敗，不能中止整份匯入。
    return null;
  }
}

/** 解析 MSPDI 日期（不含時間）字串；語意不合法（例：2 月 31 日）一律回傳 null，絕不 throw。 */
export function parseMspdiDate(value: string | undefined | null): ISODate | null {
  if (value === undefined || value === null) {
    return null;
  }
  try {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }
    const match = DATE_ONLY_RE.exec(trimmed);
    if (!match) {
      return null;
    }
    const [, y, mo, d] = match as unknown as [string, string, string, string];
    if (!isValidCalendarDateTime(Number(y), Number(mo), Number(d), 0, 0, 0)) {
      return null;
    }
    return isoDate(`${y}-${mo}-${d}`);
  } catch {
    return null;
  }
}

/** 序列化 ISODateTime 為 MSPDI 慣用格式（本身即為該格式，僅做防禦性正規化）。 */
export function formatMspdiDateTime(value: ISODateTime): string {
  const raw = rawString(value);
  const match = DATETIME_RE.exec(raw);
  if (match) {
    const [, y, mo, d, hh, mm, ss] = match;
    return `${y}-${mo}-${d}T${hh}:${mm}:${ss}`;
  }
  return raw;
}

/** 序列化 ISODate 為 MSPDI 日期字串（帶 `T00:00:00`，符合 MSPDI 慣例）。 */
export function formatMspdiDate(value: ISODate): string {
  const raw = rawString(value);
  const match = DATE_ONLY_RE.exec(raw);
  const datePart = match ? `${match[1]}-${match[2]}-${match[3]}` : raw;
  return `${datePart}T00:00:00`;
}
